import type { CartPriceSummary, CartProduct, Configuration, ConfigurationOption, DesignerPlan, DesignerPlanProduct, DesignerProductPrice, EmbedMedia, OptionList, Option, Sink, Drawing, DrawingDistance, OptionListFilter } from "./models";
import axios from "axios";
import { runExpr } from "./expr";
import { defineStore } from "pinia";

// export interface Store {
//     configuration: Configuration | null;
//     ui: UI | null;
//     cart: Cart | null;
//     state: State | null;
//     ready: boolean | null;
// }

export class State {
    [key: string]: any;

    mode: string = "worktop";

    proshop: boolean = false;

    store: boolean = false;

    fusion: boolean = false;

    prodlib: boolean = false;

    vertex: boolean = false;

    allowNewDesigner: boolean = true;

    productFamilyCode: string | null = null;

    activeDesignerPlanCode: string | null = null;

    functionalAreaCode: string = "0";

    get functionalArea() {
        if (this.productFamily == "seven")
            return "0";//"functional-area-none";

        return this.functionalAreaCode /*this.functionalAreaCode == "0"
            ? "functional-area-none"
            : this.functionalAreaCode == "L"
                ? "functional-area-left"
                : "functional-area-right";*/
    }

    set functionalArea(value: string) {
        if (this.productFamily == "seven" || this.material != "F") {
            this.functionalAreaCode = "0";
            return;
        }

        this.functionalAreaCode = value;

        if (value != "0") {
            this.graphics = "NN";
        }
        else {
            this.graphics = "00";
        }
    }

    get productFamily() {
        return this.productFamilyCode ?? "";
    }

    set productFamily(value: string | null) {
        if (value && this.productFamilyCode != value) {
            useCart().cart.resetState();
        }

        this.setFamilyWithoutReset(value);
    }

    setFamilyWithoutReset(value: string | null) {
        this.productFamilyCode = value;
        this.disabledBackStyle = value == "seven";

        if (this.disabledBackStyle)
            this.backStyle = "BackNormal";
    }

    disableAccessories: boolean = false;

    get externalEmbed() {
        return this.fusion || this.vertex || this.store || this.prodlib;
    }

    isProshopUser: boolean = false;

    disabledWorktopLocation: boolean = false;

    restrictRaisedBackOnIsland: boolean = false;

    worktopWidth: string | null = null;

    worktopHeight: string | null = null;

    worktopLength: string | null = null;

    worktopLocationCode: string | null = null;

    get worktopLocation(): string | null {        
        return this.disabledWorktopLocation
            ? "Island"
            : this.worktopLocationCode;
    }
    set worktopLocation(value: string | null) {
        if (this.disabledWorktopLocation)
            return;

        if (this.restrictRaisedBackOnIsland && value == "Island" && this.backStyle == "BackRaised") {
            this.backStyleCode = "C";
        }

        this.worktopLocationCode = value;
    }

    get backpanelReservedCode(): string { return "X" }
    set backpanelReservedCode(value: string) { }

    get backpanelReservedCode2(): string { return "X" }
    set backpanelReservedCode2(value: string) { }

    backpanelStructure: string | null = null;

    backpanelLength: string | null = null;

    backpanelHeight: string | null = null;

    get trap(): string {
        let noTrapCode = this.sink && this.sink != "H" && this.sink != "T" 
            ? "0" 
            : "X";

        return this.accessories.indexOf("trap") >= 0 ? "V" : noTrapCode;
    }
    set trap(value: string) {
        let index = this.accessories.indexOf("trap");

        if(value == "V") {
            if (index < 0) {
                this.accessories.push("trap");
            }
        }
        else {        
            if(index >= 0)
                this.accessories.splice(index, 1);
        }
    } 

    edgeProfileCode: string | null = null;

    get edgeProfile(): string {
        let edgeProfileCodes = !this.backStyle || this.backStyle == "BackNormal"
            ? this.worktopLocation == "Island" ? [ "B", "A" ] : [ "-", "S" ] // Normal back
            : [ "C", "D" ]; // Raised back

        return edgeProfileCodes[this.edgeProfileCode == "Raised" ? 0 : 1];
    }
    set edgeProfile(value: string) {
        if (!value) {
            this.edgeProfileCode = null;
            this.worktopLocation = null;

            return;
        }

        this.edgeProfileCode = (value == "-" || value == "B" || value == "C" ? "Raised" : "Straight");
        this.worktopLocation = (value == "-" || value == "S" ? "AgainstWall" : "Island");

        if (this.restrictRaisedBackOnIsland && (value == "C" || value == "D") && this.worktopLocation == "Island")
            this.worktopLocation = "AgainstWall";

        this.backStyle = (value == "C" || value == "D" ? "BackRaised" : "BackNormal");
    }

    _disabledBackStyle: boolean = false;

    get disabledBackStyle(): boolean {
        return this._disabledBackStyle;
    }

    set disabledBackStyle(value: boolean) {
        this._disabledBackStyle = value;

        if (this._disabledBackStyle && this.backStyle == "BackRaised")
            this.backStyle = "BackNormal";
    }

    backStyleCode: string | null = null;

    get backStyle(): string | null {
        if (this.backStyleCode == null)
            return null;

        return this.backStyleCode == null || this.backStyleCode == "C" ? "BackNormal" : "BackRaised";
    }
    set backStyle(value: string) {
        if (!value) {
            this.backStyleCode = null;
            return;
        }

        // Raised back can only be against wall.
        this.backStyleCode = value == "BackNormal"
            ? "C"
            : "D" //(value == "C" || value == "D" ? "BackRaised" : "BackNormal");
    }

    material: string | null = null;

    graphics: string | null = null;

    sink: string | null = null;

    sinkHandedness: string | null = null;

    sinkOrientation: string | null = null;

    m1Structure: string = "0";

    //Distance to the edge of the worktop from the handedness side
    //Left handed sink = sinkDistanceX is from left side
    sinkDistanceX: string | null = null;

    sinkDistanceMeasuredTo: string = "edge";

    hobcutoutDistanceMeasuredTo: string = "edge";

    hobcutout: string | null = null;

    hobcutoutCornerRadius: string = "0";

    hobcutoutLength: string | null = null;

    hobcutoutWidth: string | null = null;

    hobcutoutDistanceY: string | null = null;

    hobcutoutDistanceX: string | null = null;

    faucetHole: string = "0";

    get faucetHoleCode(): string {
        return this.faucetHole;
    }
    set faucetHoleCode(value: string) {
        this.faucetHole = !value ? "0" : value;
    }

    extraHole: string = "0";

    extraHole2: string = "0";

    get extraHoleCode(): string {
        if (this.extraHole && this.extraHole != "0" && this.extraHole2 && this.extraHole2 != "0")
            return "H";

        if (this.extraHole != "0")
            return this.extraHole;

        return this.extraHole2;
    }
    set extraHoleCode(hole: string) {
        if (!hole) {
            this.extraHole = "0";
            this.extraHole2 = "0";

            return;
        }

        if (hole == "H") {
            this.extraHole = "L";
            this.extraHole2 = "R";

            return;
        }

        this.extraHole = hole;
        this.extraHole2 = "0";
    }

    accessories: string[] = [];

    //Contains default values for designer code generation
    //Do not mix up with actual default values in the UI
    get defaults() {
        return {
            faucetHoleCode: "0",
            extraHoleCode: "0",
            material: "0",
            sinkDistanceX: "0000",
            trap: "0",
            hobcutout: "0",
            hobcutoutLength: "0000",
            hobcutoutWidth: "000",
            hobcutoutCornerRadius: "0",
            hobcutoutDistanceX: "0000",
            hobcutoutDistanceY: "00",
            m1Structure: "0",
            graphics: "00",
            functionalAreaCode: "0"
        } as State;
    }

    dirty: boolean = false;
}

export class Cart {
    // constructor(private state: State, public ui: UI, public config: Configuration)  {
    //     this.populateSummary();
    //  }

    // state: State | null = null;
    // ui: UI | null = null;
    // config: Configuration | null = null;

    products: CartProduct[] = [];
    cartProducts: CartProduct[] = [];
    planCode: string | null = null;

    init(state: State, ui: UI, config: Configuration) {
        // this.state = state;
        // this.ui = ui;
        // this.config = config;

        this.populateSummary();
    }

    get productCount() {
        return Math.max(this.products.length, this.cartProducts.length);
    }

    get numberOfDecimals(): number {
        return useUI().ui.language == "sv" || useUI().ui.language == "nb"
            ? 0
            : 2;
    }

    async populateSummary() {
        this.products = [];
        this.planCode = null;

        if (!useState().state) {
            return;
        }

        if (useState().state && useState().state.mode != '' &&
            ((!this.isCurrentCodeValidWorktopCode && !this.isCurrentCodeValidBackpanelCode) ||
            (this.isCurrentCodeValidWorktopCode && (useUI().ui.sinkHasPlacementError || !useUI().ui.sinkIsValid || useUI().ui.hobcutoutIsInvalid || useUI().ui.hobcutoutHasPlacementError)))) {
            this.products = this.cartProducts;

            return;
        }

        let product = await this.getCurrentCartProduct();

        if (product != null) {
            this.products.push(product);
        }

        await this.setAdditionalCostProducts();        

        let additionalCostProductcodes = useConfiguration().configuration!.conditionalAdditionalCostProducts 
            ? useConfiguration().configuration!.conditionalAdditionalCostProducts.map(p => p.code)
            : null;

        this.products = this.cartProducts.concat(this.products);

        let productsWithoutAdditionalCost = this.products.filter(p => additionalCostProductcodes == null || additionalCostProductcodes.indexOf(p.code) < 0);

        if (this.cartProducts.length == 0 && useState().state!.accessories.length == 0 && this.products.length < 2) {
            this.planCode = this.currentDesignerCode;
        }
        else if ((productsWithoutAdditionalCost.length == 1 && this.products[0]?.accessories?.length == 0) || useState().state!.vertex) {
            this.planCode = this.products[0].code;
        }
        else {
            let plan = await this.createPlan();

            this.planCode = plan.code;
        }
    }

    async addCurrentDesignToCart() {
        let product = await this.getCurrentCartProduct();

        if (product != null) {
            product.removable = true;

            if (product.accessories)
                product.accessories.forEach(a => a.removable = true);

            this.cartProducts.push(product);
        }
    }

    get hasBackpanelsInCart() {
        return this.products && this.products.filter(p => this.isValidBackpanelCode(p.code)).length > 0;
    }

    resetCart() {
        this.cartProducts = [];
    }

    resetState() {
        let properties = [ ...this.codeProperties, ...this.backpanelCodeProperties ];

        for (let prop of properties) {
            useState().state![prop] = null;
        }

        useState().state!.dirty = this.cartProducts.length > 0;

        useState().state!.accessories = [];
        useState().state.productFamily = null;

        //this.products = [];
    }

    async getPlan(code: string) {
        let { data } = await axios.get<DesignerPlan>("GetPlan?planCode=" + code);

        return data;
    }

    async createPlan() {
        let additionalCostProductcodes = useConfiguration().configuration!.conditionalAdditionalCostProducts 
            ? useConfiguration().configuration!.conditionalAdditionalCostProducts.map(p => p.code)
            : null;
            
        let productsWithoutAdditionalCost = this.products.filter(p => additionalCostProductcodes == null || additionalCostProductcodes.indexOf(p.code) < 0);

        let products = productsWithoutAdditionalCost.map(p => {
                return {
                code: p.code,
                accessories: p.accessories?.filter(a => a.code).map(a => { return a.code })
            };
        });

        let { data } = await axios.post<DesignerPlan>("CreatePlan", {
            products: products
        });

        return data;
    }

    async removeProductFromCart(product: CartProduct) {
        let index = this.cartProducts.indexOf(product);

        //if the product isn't found in the cart, the user is attempting to remove the current product that is yet to be added to cart
        if (index == -1) {
            await this.cancelCurrentDesign();
        }
        else {
            this.cartProducts.splice(index, 1);
        }

        await this.populateSummary();
    }

    async removeAccessoryProductFromCart(product: CartProduct, accessory: CartProduct) {
        if (this.cartProducts.indexOf(product) == -1) {
            let index = useState().state!.accessories.indexOf(accessory.code);

            useState().state!.accessories.splice(index, 1);
        }
        else {
            let index = product.accessories?.indexOf(accessory) ?? 0;

            product.accessories?.splice(index, 1);
        }

        await this.populateSummary();
    }

    async refreshCart() {
        for (let product of this.products) {
            let { data } = await axios.get<DesignerProductPrice>("GetPrice?code=" + product.code + "&language=" + useUI().ui.language);

            product.price = data.price;

            if (product.accessories && product.accessories.length > 0) {
                for (let accessory of product.accessories) {
                    let { data } = await axios.get<DesignerProductPrice>("GetPrice?code=" + accessory.code + "&language=" + useUI().ui.language);

                    let accessoryModel = useConfiguration().configuration!.models.accessories.find(a => a.code == accessory.code);

                    accessory.price = data.price;
                    accessory.name = accessoryModel?.name ?? "";
                }
            }
        }
    }

    async addPlanProductToCart(product: DesignerPlanProduct) {
        let values: any = [];
        let properties: string[] = [];
        let tempState: any = {};
        let accessories: CartProduct[] = [];
        let isBackpanelProduct = false;

        if (this.isValidWorktopCode(product.designerCode)) {
            values = this.designerCodeRegex.exec(product.designerCode);
            properties = this.codeProperties;
        }
        else if (this.isValidBackpanelCode(product.designerCode)) {
            values = this.backpanelCodeRegex.exec(product.designerCode);
            properties = this.backpanelCodeProperties;

            isBackpanelProduct = true;
        }

        values.splice(0, 1); //First item is the full match, don't need it

        if(!values || values.length <= 0)
            return;

        for (let i = 0; i < values.length; i++) {
            let property = properties[i];
            let value = values[i];

            tempState[property] = value;
        }

        let { data } = await axios.get<DesignerProductPrice>("GetPrice?code=" + product.designerCode + "&language=" + useUI().ui.language);

        if (product.accessories && product.accessories.length > 0) {
            for (var accessory of product.accessories.split(",")) {
                let accessoryModel = useConfiguration().configuration!.models.accessories.find(a => a.code == accessory);

                if (accessoryModel == null)
                    continue;

                let { data } = await axios.get<DesignerProductPrice>("GetPrice?code=" + accessory + "&language=" + useUI().ui.language);

                accessories.push({
                    name: accessoryModel.name,
                    dimensions: null,
                    code: accessoryModel.code,
                    price: data.price,
                    removable: true,
                    accessories: null
                });
            }
        }

        let cartProduct: CartProduct = {
            name: null,
            removable: true,
            dimensions: null,
            code: product.designerCode,
            price: data.price,
            accessories: accessories
        };

        if(isBackpanelProduct) {
            cartProduct.name = useUI().ui.t("backpanel") + " " + tempState["backpanelStructure"];
            cartProduct.dimensions = parseInt(tempState["backpanelLength"]) + "x" + parseInt(tempState["backpanelHeight"]);
        }
        else {
            let sinks = useConfiguration().configuration!.models.sinks.filter(s => s.code == tempState["sink"]);

            let sink = sinks.find(s => s.handedness == tempState["sinkHandedness"]) || sinks[0];

            let worktopWidth =  useConfiguration().configuration!.models.worktopWidths.find(s => s.code == tempState["worktopWidth"]);
            let worktopHeight =  useConfiguration().configuration!.models.worktopHeights.find(s => s.code == tempState["worktopHeight"]);

            cartProduct.name = sink.name;
            cartProduct.dimensions = tempState["worktopLength"] + "x" + worktopWidth?.width + "x" + worktopHeight?.height;
        }

        this.cartProducts.push(cartProduct);
    }

    async setAdditionalCostProducts() {
        if (!useConfiguration().configuration!.conditionalAdditionalCostProducts)
            return;

        for (let conditionalProduct of useConfiguration().configuration!.conditionalAdditionalCostProducts) {
            let condition = new RegExp(conditionalProduct.condition);

            let productsWithAdditionalCost = this.products.concat(this.cartProducts).filter(p => condition.test(p.code));

            if (productsWithAdditionalCost.length > 0) {
                let { data } = await axios.get<DesignerProductPrice>("GetPrice", { params: { code: conditionalProduct.code,  language: useUI().ui.language, quantity: productsWithAdditionalCost.length }});

                this.products.push({
                    name: conditionalProduct.name,
                    removable: false,
                    dimensions: null,
                    code: conditionalProduct.code,
                    price: data.price,
                    accessories: []
                })
            }
        }
    }

    async getCurrentCartProduct() {
        if (!useState().state || useState().state!.mode == '')
            return null;

        let accessories: CartProduct[] = [];

        if (useState().state!.accessories.length > 0) {
            for (var accessory of useState().state!.accessories) {
                let accessoryConfig = useConfiguration().configuration!.models.accessories.find(a => a.code == accessory);

                if (accessoryConfig == null)
                    continue;

                let accessoryModel: CartProduct = {
                    name: accessoryConfig.name,
                    dimensions: null,
                    removable: true,
                    price: null,
                    code: accessoryConfig.code,
                    accessories: null
                };

                if (!useUI().ui.hidePrices) {
                    let { data } = await axios.get<DesignerProductPrice>("GetPrice?code=" + accessory + "&language=" + useUI().ui.language);

                    accessoryModel.price = data.price;
                }

                accessories.push(accessoryModel);
            }
        }

        let product: CartProduct = {
            code: this.currentDesignerCode,
            price: null,
            name: null,
            dimensions: null,
            removable: true,
            accessories: accessories
        };

        if(this.isValidWorktopCode(this.currentDesignerCode)) {
            product.name = useUI().ui.sink.name;
            product.dimensions = useUI().ui.worktopLength + "x" + useUI().ui.worktopWidth + "x" + useUI().ui.worktopHeight;
        }
        else if(this.isValidBackpanelCode(this.currentDesignerCode)) {
            product.name = useUI().ui.t("backpanel") + " " + useState().state!.backpanelStructure;
            product.dimensions = useUI().ui.backpanelLength + "x" + useUI().ui.backpanelHeight;
        }

        if (!useUI().ui.hidePrices) {
            let { data } = await axios.get<DesignerProductPrice>("GetPrice?code=" + this.currentDesignerCode + "&language=" + useUI().ui.language);

            product.price = data.price;
        }

        return product;
    }

    get currencyCode() {
        return useConfiguration().configuration!.currencyCode;
    }

    get taxPercent() {
        return useConfiguration().configuration!.taxPercent;
    }

    get priceSummary(): CartPriceSummary | null {
        if (!this.products || this.products.length == 0 || useUI().ui.hidePrices)
            return null;

        let taxPercent = useConfiguration().configuration!.taxPercent;
        let taxFactor = taxPercent / 100 + 1;

        let subtotal = this.products.reduce((s, p) => { return s + (p.price ?? 0) + p.accessories!.reduce((as, a) => { return as + (a.price ?? 0) }, 0) }, 0);

        let tax = subtotal * taxFactor - subtotal;

        return {
            subTotal: subtotal,
            taxPercent: taxPercent,
            tax: tax,
            grandTotal: subtotal + tax
        };
    }

    get backpanelCodeProperties(): string[] {
        return [
            "backpanelStructure", "material", "backpanelReservedCode",
            "backpanelLength", "backpanelReservedCode2", "backpanelHeight", "graphics"
        ];
    }

    get codeProperties(): string[] {
        return [ ...this.mandatoryCodeProperties, ...this.optionalCodeProperties ];
    }

    get mandatoryCodeProperties(): string[] {
        return [
            "sink", "worktopLength", "worktopHeight", "sinkHandedness", "edgeProfile", "sinkDistanceX", "worktopWidth"
        ];
    }

    get optionalCodeProperties(): string[] {
        return [
            "faucetHoleCode", "extraHoleCode", "material",
            "trap", "hobcutout", "hobcutoutLength", "hobcutoutWidth",
            "hobcutoutCornerRadius", "hobcutoutDistanceX", "hobcutoutDistanceY",
            "m1Structure", "graphics", "functionalAreaCode"
        ];
    }

    get isCurrentCodeValidWorktopCode(): boolean {
        return this.isValidWorktopCode(this.currentDesignerCode);
    }

    get isCurrentCodeValidBackpanelCode(): boolean {
        return this.isValidBackpanelCode(this.currentDesignerCode);
    }

    get designerCodeRegex(): RegExp {
        return new RegExp(useConfiguration().configuration!.designerCodeRegexp);
    }

    get backpanelCodeRegex(): RegExp {
        return new RegExp(useConfiguration().configuration!.backpanelCodeRegexp);
    }

    isValidWorktopCode(code: string): boolean {        
        return this.designerCodeRegex.test(code);
    }

    isValidPlanCode(code: string): boolean {
        return code.startsWith("PLAN-");
    }

    isValidBackpanelCode(code: string): boolean {
        return this.backpanelCodeRegex.test(code);
    }

    trimEnd(string: string, charToRemove: string) {
        while(string.charAt(string.length-1)==charToRemove) {
            string = string.substring(0,string.length-1);
        }

        return string;
    }

    fillState(code: string) {
        let values: RegExpExecArray | null = null;
        let properties: string[] = [];

        const sevenSinkCodes = "ACFLNH";
        const oneSinkCodes = "DPSMKBT"
        const { state } = useState();

        if (this.isValidWorktopCode(code)) {
            values = this.designerCodeRegex.exec(code);
            properties = this.codeProperties;

            if (sevenSinkCodes.includes(code.charAt(0)))
                state.productFamilyCode = "seven";
            else if (oneSinkCodes.includes(code.charAt(0)))
                state.productFamilyCode = "one";

            if (state.productFamilyCode == "seven") {
                state.disabledBackStyle = true;
                state.backStyle = "BackNormal";
            }

            state!.mode = "worktop";

        }
        else if (this.isValidBackpanelCode(code)) {
            values = this.backpanelCodeRegex.exec(code);
            properties = this.backpanelCodeProperties;

            useState().state!.mode = "backpanel";
        }

        if(!values || !properties)
            return;

        values.splice(0, 1); //First item is the full match, don't need it

        for (let i = 0; i < values.length; i++) {
            let property = properties[i];
            let value: string | null = values[i];

            if (value && this.trimEnd(value, "0") == '')
                value = null;

            useState().state![property] = value;
        }

        let fillableDefaults = [ "material", "hobcutout" ];

        for (let p of fillableDefaults) {
            if (!useState().state![p]) {
                useState().state![p] = "0";
            }
        }
    }

    get currentDesignerCode(): string {
        if (useState().state!.mode == "worktop") {
            let mandatoryProperties = this.mandatoryCodeProperties.map(p => useState().state![p] || useState().state!.defaults[p]).join("");

            let optionalProperties = this.optionalCodeProperties.map(p => useState().state![p] || useState().state!.defaults[p]);

            // If user chose raised back, force packing code to be LA.
            // if (useState().state.backStyle == "BackRaised") {
            //     optionalProperties.push(
            //         "0".repeat(10), // [37-46] Unused
            //         "LA"            // [47-48] Packing code
            //     );  
            // }

            //Remove unnecessary 0's at the end of the code
            for (let i = optionalProperties.length - 1; i >= 0; i--) {
                if (this.trimEnd(optionalProperties[i], "0") == "") {
                    optionalProperties.pop();
                }
                else {
                    break;
                }
            }

            return mandatoryProperties + optionalProperties.join("");
        }
        else if (useState().state!.mode == "backpanel") {
            return this.backpanelCodeProperties.map(p => useState().state![p] || useState().state!.defaults[p]).join("");
        }
        return "";
    }

    async cancelCurrentDesign() {
        this.resetState();

        useState().state!.dirty = false;

        useState().state!.mode = "";
    }

    isAdditionalCostProduct(code: string) {
        return code == "P" || (useConfiguration().configuration!.conditionalAdditionalCostProducts && useConfiguration().configuration!.conditionalAdditionalCostProducts.filter(c => c.code == code).length > 0);
    }

    async setDesignerCode(code: string) {
        if (!code)
            return;

        code = code.trim();

        let designerCode = code;
        useState().state.activeDesignerPlanCode = code;

        this.resetState();
        this.resetCart();


        if (this.isValidPlanCode(code)) {
            let plan = await this.getPlan(code);

            if (plan != null) {
                // let product = plan.products[plan.products.length - 1];
                let designableProducts = plan.products.filter(p => !this.isAdditionalCostProduct(p.designerCode));

                let product = designableProducts[designableProducts.length - 1];

                if (designableProducts.length > 1) {
                    for (let i = 0; i < designableProducts.length - 1; i++) {
                        await this.addPlanProductToCart(designableProducts[i]);
                    }
                }

                useState().state!.accessories = product.accessories ? product.accessories.split(",") : [];

                designerCode = product.designerCode;
            }
        }

        useState().state!.dirty = true;

        await this.fillState(designerCode);
    }
}

export class UI {
    // constructor(private state: State, private config: Configuration)  { }

    state: State | null = useState().state;
    config: Configuration | null = null;

    helpVisible: boolean = false;
    minicartVisible: boolean = false;
    languagesVisible: boolean = false;
    navigateToStartPopupOpen: boolean = false;
    exitConfirmed: boolean = false;
    isTabletView: boolean = false;
    embedPopupOpen: boolean = false;
    embedMedia: EmbedMedia[] | null = null;
    activeEmbedMedia: EmbedMedia | null = null;
    activeEmbedVideoUrl: string | null = null;

    get self(): UI {
        return this;
    }

    init(state: State | null, config: Configuration | null) {
        this.state = state;
        this.config = config;

        this.helpVisible = false;
        this.minicartVisible = false;
        this.languagesVisible = false;
        this.navigateToStartPopupOpen = false;
        this.exitConfirmed = false;
        this.isTabletView = false;
        this.embedPopupOpen = false;
        this.embedMedia = null;
        this.activeEmbedMedia = null;
        this.activeEmbedVideoUrl = null;
    }

    isExprFilter(filter: OptionListFilter) {
        return typeof filter === "object" && filter !== null && "expr" in filter && "disable" in filter;
    }

    private generateOptionList(key: string, configurationOptions: ConfigurationOption[], title: string | null, filter: OptionListFilter | null = null, disableFiltered: boolean = false, disableText: string | null = null): OptionList {
        let options = configurationOptions;

        if (!this.state?.allowNewDesigner) {
            options = options.filter(o => !o.productFamilies.length || !o.productFamilies.every(f => f == "one"));
        }

        if (filter) {
            if (disableFiltered) {
                options.forEach(o => { 
                    o.disabled = !filter(o)
                    o.disableText = disableText;
                });
            }
            else {
                options = options.filter(filter);
            }
        }
        
        return {
            title: title,
            options: options
            .map(co => {
                let disabled = false;
                let messages: any[] = [];
                let warnings: any[] = [];

                if (co.rules) {
                    for (let rule of co.rules) {
                        let enabled = runExpr({
                            state: this.state,
                            ui: this.self,
                            selfKey: key
                        }, rule.expression);

                        if (!enabled) {
                            disabled = true;

                            if (messages.indexOf(rule.resourceKey) == -1)
                                messages.push(rule.resourceKey);
                        }
                    }
                }

                if(co.warnings) {
                    for (let warning of co.warnings) {
                        let hasWarning = runExpr({
                            state: this.state,
                            ui: this.self,
                            selfKey: key
                        }, warning.expression);

                        if (hasWarning && warnings.indexOf(warning.resourceKey) == -1) {
                            warnings.push(warning.resourceKey);
                        }
                    }
                }

                return {
                    value: co.code,
                    text: co.name,
                    link: co.link,
                    image: co.image,
                    embedMedia: co.embedMedia,
                    visible: !this.state!.vertex || co.visibleInVertex,
                    disabled: co.disabled || disabled,
                    warnings: warnings,
                    messages: messages,
                    disableText: co.disableText
                };
            })
            .filter(o => o.visible)
        }
    }

    filterWidthsByMaterial(option: ConfigurationOption) {
        if (this.state?.material == "F")
            return option.width >= 590 && option.width <= 610;

        return true;
    }

    get worktopWidths(): OptionList {
        return this.generateOptionList("worktopWidth", this.config!.models.worktopWidths, this.t("depth"), this.filterWidthsByMaterial.bind(this), true);
    }

    get worktopHeights(): OptionList {
        return this.generateOptionList("worktopHeight", this.config!.models.worktopHeights, this.t("worktopHeight"));
    }

    get worktopLocations(): OptionList {
        return this.generateOptionList("worktopLocation", this.config!.models.worktopLocations, null);
    }

    filterBackStylesByMaterial(option: ConfigurationOption) {
        return option.code != "BackRaised" || this.state?.material !== "F";
    }

    get worktopBackStyles(): OptionList {
        const list = this.generateOptionList("worktopBackStyle", this.config!.models.worktopBackStyles, null, this.filterBackStylesByMaterial.bind(this), true);

        if (this.state!.worktopLocationCode == "Island") {
            const raisedBackOption = list.options.find(o => o.value == "BackRaised");
            if (raisedBackOption)
                raisedBackOption.disabled = this.state?.restrictRaisedBackOnIsland || false;
        }

        return list;
    }

    get accessories(): OptionList {
        const list = this.generateOptionList("accessories", this.config!.models.accessories, null, a => a.productFamilies.includes(this.state!.productFamily!));
        list.options.sort(o => o.value?.toUpperCase().startsWith("TAP") ? 1 : -1);
        return list;
    }

    get edgeProfiles(): OptionList {
        return this.generateOptionList("edgeProfile", this.config!.models.edgeProfiles, null, this.filterEdgeProfiles.bind(this), true, this.t("notWithFunctionalArea"));
    }

    filterEdgeProfiles(e: ConfigurationOption) {
        return e.code == "Straight" || !this.state?.functionalArea || this.state.functionalArea == "0"; 
    }

    get materials(): OptionList {
        const familyMaterials = this.config?.models.materials.filter(m => m.productFamilies.includes(this.state?.productFamily!)) ?? [];
        
        if (!this.state?.functionalArea || this.state.functionalArea == "0")
            return this.generateOptionList("material", familyMaterials, null, m => true, true);

        // With functional area, we have to disable everything other than matte
        return this.generateOptionList("material", familyMaterials, null, m => m.code == "F", true, this.t('functionalAreaOnlyAvailableForOneMaterial'))
    }

    get faucetHoles(): OptionList {
        return this.generateOptionList("faucetHole", this.config!.models.faucetHoles, this.t("sinkFaucetHole"));
    }

    get extraHoles(): OptionList {
        return this.generateOptionList("extraHole", this.config!.models.extraHoles, this.t("sinkExtraHole"));
    }

    get extraHoles2(): OptionList {
        return this.generateOptionList("extraHole2", this.config!.models.extraHoles, this.t("sinkExtraHole"));
    }

    get sinks(): OptionList {
        return this.generateOptionList("sink", this.config!.models.sinks, null, s => s.productFamilies.includes(this.state!.productFamily!));
    }

    get backpanelStructures(): OptionList {
        return this.generateOptionList("backpanelStructure", this.config!.models.backpanelStructures, null);
    }

    get backpanelMaterial(): OptionList {
        return this.generateOptionList("backpanelMaterial", this.config!.models.backpanelMaterial, null);
    }

    get hobcutouts(): OptionList {
        return this.generateOptionList("hobcutout", this.config!.models.hobcutouts, null);
    }

    get functionalAreaOptions(): OptionList {
        return this.generateOptionList("functionalAreaOptions", this.config!.models.functionalAreaOptions, null, this.filterFunctionalAreaOptions.bind(this), true, this.t("notWithRaisedEdge"));
    }

    filterFunctionalAreaOptions(e: ConfigurationOption) {
        return this.state?.edgeProfileCode == "Straight" || e.code == "0";
    }

    get translations() {
        return this.config!.translations;
    }

    get languages() {
        return this.config!.languages.filter(l => l != this.language);
    }

    get language() {
        return this.config!.language;
    }

    get hidePrices() {
        return this.config!.hidePrices || this.state!.vertex;
    }

    get chatEnabled() {
        return this.config!.chatEnabled;
    }

    get images() {
        return this.config!.images;
    }

    get blocks() {
        return this.config!.blocks;
    }

    get texts() {
        return this.config!.texts;
    }

    currentFocusedField: string | null = "";
    lastFocusedField: string | null = "";

    get focusedFieldName() : string | null {
        return this.currentFocusedField;
    }
    set focusedFieldName(value: string | null) {
        this.currentFocusedField = value;

        if (value)
            this.lastFocusedField = value;
    }

    toggleHelp() {
        this.helpVisible = !this.helpVisible;
    }

    openEmbedPopup(media: EmbedMedia[]) {
        this.embedMedia = media;

        this.activeEmbedMedia = media.length > 0 ? media[0] : null;

        this.embedPopupOpen = true;
    }

    get visibleSelections(): Option[] {
        let selections: Option[] = [];

        if (this.state!.graphics || this.state!.material) {
            let code = this.state!.graphics || this.state!.material;

            var material = this.materials.options.find(o => o.value == code);

            if (material != undefined)
                selections.push(material);
        }

        // if (this.state!.accessories) {
        //     var accessories = this.accessories.options.filter(o => this.state!.accessories.indexOf(o.value) != -1);

        //     selections = [...selections, ...accessories ];
        // }

        return selections;
    }

    get hobcutoutIsInvalid(): boolean {
        return this.state!.hobcutout == "1" && this.hobcutoutHasPlacementError
    }

    get productFamilySelectionComplete(): boolean {
        return !!this.state!.productFamily;
    }

    get backpanelSelectionsComplete(): boolean {
        return !!this.state!.backpanelStructure && !!this.state!.backpanelHeight && !!this.state!.backpanelLength;
    }

    get backpanelMaterialSelectionsComplete(): boolean {
        return this.materialSelectionsComplete;
    }

    get functionalAreaSelectionsComplete(): boolean {
        if (!this.state)
            return false;

        return !!this.state.functionalArea;
    }

    get materialSelectionsComplete(): boolean {
        if (!this.state) {
            return false;
        }

        let selections = [ this.state!.material ];

        return selections.filter(s => !s).length == 0;
    }

    get hobcutoutSelectionsComplete(): boolean {
        if (!(this.state && this.state.hobcutout)) {
            return false;
        }

        if (this.state!.hobcutout == "0")
            return true;

        let selections = [ this.state!.hobcutout, this.state!.hobcutoutDistanceY, this.state!.hobcutoutDistanceX, this.state!.hobcutoutLength, this.state!.hobcutoutWidth, !this.hobcutoutIsInvalid ];

        return selections.filter(s => !s).length == 0;
    }

    get worktopSelectionsComplete(): boolean {
        let selections = [ this.state!.worktopLength, this.state!.worktopWidth, this.state!.worktopHeight, this.state!.edgeProfileCode, this.state!.worktopLocation ];

        if (this.state?.productFamily == "one") 
            selections.push(this.state!.backStyle);

        return selections.filter(s => !s).length == 0 && this.drawing.ready;
    }

    getImageUrl(src: string, maxWidth: number) {
        return src
            ? src + "?__imgop=1&maxwidth=" + maxWidth
            : null;
    }

    getBackgroundImageUrl(src: string, maxWidth: number) {
        return src
            ? "url('" + this.getImageUrl(src, maxWidth) + "')"
            : null;
    }

    get backpanelStructure() {
        return this.config!.models.backpanelStructures.find(s => s.code == this.state!.backpanelStructure);
    }

    get backpanelLength() : number | null { return !this.state!.backpanelLength ? null : parseInt(this.state!.backpanelLength) }
    set backpanelLength(value: number) { this.state!.backpanelLength = padLeft(value, 4, "0") }

    get backpanelHeight() : number | null { return !this.state!.backpanelHeight ? null : parseInt(this.state!.backpanelHeight) }
    set backpanelHeight(value: number) { this.state!.backpanelHeight = padLeft(value, 4, "0") }

    get worktopWidth() { return (this.config!.models.worktopWidths.find(w => w.code == this.state!.worktopWidth) || { } as any).width }
    get worktopHeight() { return (this.config!.models.worktopHeights.find(w => w.code == this.state!.worktopHeight) || {} as any).height }

    get worktopLength() : number | null { return !this.state!.worktopLength ? null : parseInt(this.state!.worktopLength) }
    set worktopLength(value: number) { this.state!.worktopLength = padLeft(Math.ceil(value / 5) * 5, 4, "0") }


    get hobcutoutDistanceRight(): number | null {
        if (!this.state!.hobcutoutDistanceX)
            return null;

        let shortSideDistance = parseInt(this.state!.hobcutoutDistanceX);

        if (!this.sink.leftHanded)
            return shortSideDistance;

        if (this.worktopLength != null && this.hobcutoutLength != null)
            return this.worktopLength - this.hobcutoutLength - shortSideDistance;
        
        return null;
    }
    
    set hobcutoutDistanceRight(value: number) {
        let distanceRight = this.getSafeHobcutoutDistanceX(value, false);

        this.state!.hobcutoutDistanceX = !this.sink.leftHanded
            ? padLeft(distanceRight, 4, "0")
            : padLeft((this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - (distanceRight ?? 0), 4, "0");
    }

    get hobcutoutDistanceRightCenter(): number | null {
        if (!this.state!.hobcutoutDistanceX)
            return null;

        return !this.sink.leftHanded
            ? parseInt(this.state!.hobcutoutDistanceX) + Math.floor((this.hobcutoutLength ?? 0) / 2)
            : (this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - parseInt(this.state!.hobcutoutDistanceX) + Math.floor((this.hobcutoutLength ?? 0) / 2);
    }

    set hobcutoutDistanceRightCenter(value: number) {
        let edgeValue = value - Math.floor((this.hobcutoutLength ?? 0) / 2);
        let distanceRight = this.getSafeHobcutoutDistanceX(edgeValue, false);

        this.state!.hobcutoutDistanceX = !this.sink.leftHanded
            ? padLeft(distanceRight, 4, "0")
            : padLeft((this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - (distanceRight ?? 0), 4, "0");
    }

    get hobcutoutDistanceLeft(): number | null {
        if (!this.state!.hobcutoutDistanceX)
            return null;

        let shortSideDistance = parseInt(this.state!.hobcutoutDistanceX);

        if (this.sink.leftHanded)
            return shortSideDistance;

        return (this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - shortSideDistance;
    }
    set hobcutoutDistanceLeft(value: number) {
        let distanceLeft = this.getSafeHobcutoutDistanceX(value, true);

        this.state!.hobcutoutDistanceX = this.sink.leftHanded
            ? padLeft(distanceLeft, 4, "0")
            : padLeft((this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - (distanceLeft ?? 0), 4, "0");

    }

    get hobcutoutDistanceLeftCenter(): number | null {
        if (!this.state!.hobcutoutDistanceX)
            return null;

        return this.sink.leftHanded
            ? parseInt(this.state!.hobcutoutDistanceX) + Math.floor((this.hobcutoutLength ?? 0) / 2)
            : (this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - parseInt(this.state!.hobcutoutDistanceX) + Math.floor((this.hobcutoutLength ?? 0) / 2);
    }
    set hobcutoutDistanceLeftCenter(value: number) {
        let edgeValue = value - Math.floor((this.hobcutoutLength ?? 0) / 2);
        let distanceLeft = this.getSafeHobcutoutDistanceX(edgeValue, true);

        this.state!.hobcutoutDistanceX = this.sink.leftHanded
            ? padLeft(distanceLeft, 4, "0")
            : padLeft((this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - (distanceLeft ?? 0), 4, "0");
    }

    //Collision between sink and hobcutout
    hasCollision(sinkDistanceLeft: number | null, hobcutoutDistanceLeft: number | null): boolean {
        let leftMargin = this.hobcutoutMargins;
        let rightMargin = this.hobcutoutMargins;

        if (this.state?.functionalAreaCode == "L") {
            leftMargin = 245;
            rightMargin = 100;
        }
        else if (this.state?.functionalAreaCode == "R") {
            leftMargin = 100;
            rightMargin = 245;
        }

        if (sinkDistanceLeft && (this.hobcutoutLength ?? 0) > 0 && (this.hobcutoutDistanceLeft ?? 0) > 0 && this.sink.hasDimensions) {
            return ((this.hobcutoutDistanceLeft ?? 0)) < (sinkDistanceLeft + this.sink.length + rightMargin) //right side collision
                && ((this.hobcutoutDistanceLeft ?? 0) + (this.hobcutoutLength ?? 0)) > sinkDistanceLeft - leftMargin //left side collision
        }

        if (hobcutoutDistanceLeft && this.sink.hasDimensions && (this.hobcutoutLength ?? 0) > 0) {
            return ((this.sinkDistanceLeft ?? 0) - leftMargin) < (hobcutoutDistanceLeft + (this.hobcutoutLength ?? 0)) //right side collision
                && ((this.sinkDistanceLeft ?? 0) + this.sink.length + rightMargin) > hobcutoutDistanceLeft  //left side collision
        }

        return false;
    }

    getSafeSinkDistanceX(value: number, left: boolean): number | null {
        let desiredDistanceLeft = left ? value : (this.worktopLength ?? 0)- this.sink.length - value;

        if (left && this.state?.functionalArea == "L" && desiredDistanceLeft < 225)
            return 225;
        
        if (left && this.state?.functionalArea == "R" && desiredDistanceLeft < 40)
            return 40;

        if (!left && this.state?.functionalArea == "L" && value < 40)
            return 40;
        
        if (!left && this.state?.functionalArea == "R" && value < 225)
            return 225;

        if(isNaN(value))
            return this.sink.margins;

        if (this.hasCollision(desiredDistanceLeft, null)) {
            let availableSpaces = [
                {
                    space: (this.hobcutoutDistanceLeft ?? 0) - this.sink.margins - this.hobcutoutMargins,
                    left: (this.hobcutoutDistanceLeft ?? 0) - this.sink.length - (this.state?.functionalArea == "R" ? 245 : this.hobcutoutMargins),
                    right: (this.hobcutoutDistanceRight ?? 0) + (this.hobcutoutLength ?? 0) + (this.state?.functionalArea == "R" ? 245 : this.hobcutoutMargins)
                }, //left side safe space
                {
                    space: (this.hobcutoutDistanceRight ?? 0) - this.sink.margins - this.hobcutoutMargins,
                    left: (this.hobcutoutDistanceLeft ?? 0) + (this.hobcutoutLength ?? 0) + (this.state?.functionalArea == "L" ? 245 : this.hobcutoutMargins),
                    right: (this.hobcutoutDistanceRight ?? 0) - (this.hobcutoutLength ?? 0) - (this.state?.functionalArea == "L" ? 245 : this.hobcutoutMargins)
                }  //right side safe space
            ]
            .filter(s => s.space >= this.sink.length)
            .sort((a, b) => Math.abs(desiredDistanceLeft - a.left) - Math.abs(desiredDistanceLeft - b.left));

            if (availableSpaces.length == 0) //There's no space for the hob cutout
                return null;

            return Math.ceil((left ? availableSpaces[0].left : availableSpaces[0].right) / 5) * 5;
        }

        if (this.hobcutoutLength == 0 || value < this.sink.margins)
            return Math.max(Math.ceil(value / 5) * 5, this.sink.margins);

        if (value > (this.worktopLength ?? 0) - this.sink.length - this.sink.margins)
            return this.sink.margins;

        return Math.ceil(value / 5) * 5;
    }


    // getSafeHobcutoutDistanceX(value: number, left: boolean): number | null {
    //     let desiredDistanceLeft = left ? value : (this.worktopLength ?? 0)- (this.hobcutoutLength ?? 0) - value;

    //     if(isNaN(value))
    //         return 45;

    //     if (this.hasCollision(null, desiredDistanceLeft)){//} || (this.state?.functionalArea && this.state?.functionalArea != "0")) {
    //         let leftMargin = this.hobcutoutMargins;
    //         let rightMargin = this.hobcutoutMargins;

    //         if (this.state?.functionalAreaCode == "L") {
    //             leftMargin = 245;
    //             rightMargin = 100;
    //         }
    //         if (this.state?.functionalAreaCode == "R") {
    //             rightMargin = 245;
    //             leftMargin = 100;
    //         }

    //         let availableSpaces = [
    //             {
    //                 space: (this.sinkDistanceLeft ?? 0) - 45 - leftMargin,
    //                 left: (this.sinkDistanceLeft ?? 0) - (this.hobcutoutLength ?? 0) - leftMargin,
    //                 right: (this.sinkDistanceRight ?? 0) - rightMargin - (this.hobcutoutLength ?? 0)
    //             }, //left side safe space
    //             {
    //                 space: (this.sinkDistanceRight ?? 0) - 45 - leftMargin,
    //                 left: (this.sinkDistanceLeft ?? 0) - (this.hobcutoutLength ?? 0) - leftMargin,
    //                 right: (this.sinkDistanceRight ?? 0) - rightMargin - (this.hobcutoutLength ?? 0)
    //             }//right side safe space
    //         ];

    //         availableSpaces = availableSpaces
    //         .filter(s => s.space >= (this.hobcutoutLength ?? 0))
    //         .sort((a, b) => Math.abs(desiredDistanceLeft - a.left) - Math.abs(desiredDistanceLeft - b.left));

    //         if (availableSpaces.length == 0) //There's no space for the hob cutout
    //             return null;

    //         return left ? availableSpaces[0].left : availableSpaces[0].right;
    //     }

    //     if (!this.sink.hasDimensions || value < 45)
    //         return Math.max(value, 45);

    //     if (value > (this.worktopLength ?? 0) - (this.hobcutoutLength ?? 0) - 45)
    //         return 45;

    //     return value;
    // }
    

    getSafeHobcutoutDistanceX(value: number, left: boolean): number | null {
        const worktopLength = this.worktopLength ?? 0;
        const hobLength = this.hobcutoutLength ?? 0;

        let toCenterCorrection = this.state!.hobcutoutDistanceMeasuredTo == 'center'
            ? -Math.floor(hobLength / 2)
            : 0;

        const desiredDistanceLeft = left ? value + toCenterCorrection : worktopLength - hobLength - value + toCenterCorrection;

        if (isNaN(value)) return 45;
    
        if (this.hasCollision(null, desiredDistanceLeft)) {
            let leftMargin = this.hobcutoutMargins;
            let rightMargin = this.hobcutoutMargins;
    
            if (this.state?.functionalAreaCode === "L") {
                leftMargin = 245;
                rightMargin = 100;
            } else if (this.state?.functionalAreaCode === "R") {
                leftMargin = 100;
                rightMargin = 245;
            }
    
            const sinkLeft = this.sinkDistanceLeft ?? 0;
            const sinkRight = this.sinkDistanceRight ?? 0;
    
            const availableLeft = {
                space: sinkLeft - 45 - leftMargin,
                pos: sinkLeft - hobLength - leftMargin
            };
    
            const availableRight = {
                space: sinkRight - 45 - rightMargin,
                pos: sinkRight - hobLength - rightMargin
            };
    
            const leftValid = availableLeft.space >= hobLength && availableLeft.pos >= 0;
            const rightValid = availableRight.space >= hobLength && availableRight.pos >= 0;
    
            let availablePosition = null;

            if (left && leftValid) availablePosition = availableLeft.pos;
            if (!left && rightValid) availablePosition = availableRight.pos;
    
            // Fallback, calculate from opposite side
            if (left && rightValid) availablePosition = (this.worktopLength ?? 0) - (this.hobcutoutLength ?? 0) - availableRight.pos;
            if (!left && leftValid) availablePosition = (this.worktopLength ?? 0) - availableLeft.pos;
    
            // Check if rounding would cause incorrect margins with specific sinks and add/take 5mm
            if (left && (availablePosition ?? 0) - ((this.sinkDistanceLeft ?? 0) + this.sink.length) < rightMargin) 
                availablePosition = availablePosition ? availablePosition + 5 : null;

            if (!left && ((this.worktopLength ?? 0) - (this.sinkDistanceLeft ?? 0) - this.sink.length) - ((availablePosition ?? 0) + (this.hobcutoutLength ?? 0)) < 100) 
                availablePosition = availablePosition ? availablePosition - 5 : null;

            return availablePosition 
                ? Math.ceil(availablePosition / 5) * 5 
                : null; // no space found
        }
    
        // Outside collision area: clamp to valid range
        if (!this.sink.hasDimensions || value < 45)
            return Math.ceil(Math.max(value, 45) / 5) * 5;
    
        if (value > worktopLength - hobLength - 45)
            return 45;
    
        return Math.ceil(value / 5) * 5;
    }
    

    get hobcutoutMargins() { return 100 }

    get hobcutoutMinimumLeft() {
        if (!this.sink)
            return 45;

        const hobCutoutOnLeftSide = this.sinkDistanceLeft && (this.hobcutoutDistanceLeft ?? 0) < this.sinkDistanceLeft;

        // Default sink margin towards hobcutout
        let hobcutoutMarginLeft = this.sinkMargins.right;

        if (this.state?.functionalAreaCode == "L")
            hobcutoutMarginLeft = 100;
        else if (this.state?.functionalAreaCode == "R")
            hobcutoutMarginLeft = 245;

        // If hob cutout is on left side of sink
        if (hobCutoutOnLeftSide
            // And there is space for cutout, worktop margin is minimum left value
            && (this.hobcutoutWidth ?? 0) < 45 + this.sinkDistanceLeft!) {
            return 45;
        }

        return (this.sinkDistanceLeft ?? 0) + this.sink?.width + hobcutoutMarginLeft;
    }

    get hobcutoutMaximumLeft() {
        if (!this.sink)
            return this.worktopWidth - 45;

        const hobCutoutOnLeftSide = this.sinkDistanceLeft && (this.hobcutoutDistanceLeft ?? 0) < this.sinkDistanceLeft;

        // default margin between hobcutout and sink
        let hobcutoutMarginRight = this.sinkMargins.left;

        // margin with functional area
        if (this.state?.functionalAreaCode == "L")
            hobcutoutMarginRight = 245;
        else if (this.state?.functionalAreaCode == "R")
            hobcutoutMarginRight = 100;

        // If hobcutout is on left side, maximum is sink left - margin
        if (hobCutoutOnLeftSide) {
            return this.sinkDistanceLeft! - hobcutoutMarginRight;
        }

        // If hobcutout is on right side, maximum is worktop length - minimum margin
        return this.worktopWidth - 45;
    }

    get hobcutoutHasPlacementError() {
        return this.hasCollision(null, this.hobcutoutDistanceLeft)
                || (this.hobcutoutWidth ?? 0) > this.worktopWidth - (45 * 2)
                || ((this.hobcutoutLength ?? 0) > 0 && (this.hobcutoutWidth ?? 0) > 0
                    && ((this.hobcutoutDistanceLeft ?? 0) < 45
                        || !this.hobcutoutDistanceLeft
                        || !this.hobcutoutDistanceFront
                        || ( this.hobcutoutDistanceRight ?? 0) < 45
                        || this.hobcutoutDistanceFront < 45
                        || this.hobcutoutDistanceBack < 45));
    }

    get hobcutoutDistanceBack() { return this.worktopWidth - (this.hobcutoutWidth ?? 0) - this.hobcutoutDistanceFront }

    get hobcutoutDistanceFront() { return parseInt(this.state!.hobcutoutDistanceY ?? "") }
    set hobcutoutDistanceFront(value: number) {
        this.state!.hobcutoutDistanceY = isNaN(value)
            ? padLeft(45, 2, "0")
            : padLeft(value, 2, "0");
    }

    get hobcutoutWidth(): number | null { return this.state!.hobcutoutWidth ? parseInt(this.state!.hobcutoutWidth) : null }
    set hobcutoutWidth(value: number) { this.state!.hobcutoutWidth = padLeft(value, 3, "0") }

    get hobcutoutLength(): number | null { return this.state!.hobcutoutLength ? parseInt(this.state!.hobcutoutLength) : null }
    set hobcutoutLength(value: number) {
        let availableSpaces = [
            (this.sinkDistanceLeft || (this.worktopLength ?? 0)) - 45  - (this.sinkDistanceLeft ? this.hobcutoutMargins : 45),
            (this.sinkDistanceRight  || (this.worktopLength ?? 0)) - 45 - (this.sinkDistanceRight ? this.hobcutoutMargins : 45)
        ];

        var availableSpace = availableSpaces.find(s => s >= value);

        let length = availableSpace
            ? value
            : Math.max(...availableSpaces);

        this.state!.hobcutoutLength = padLeft(length, 4, "0");
    }

    get functionalAreaLeftAvailable() {
        const sinkLeftEdgePosition = this.sinkDistanceLeft ?? 0;

        if (!this.state?.hobcutout || this.state.hobcutout == "0") {
            return sinkLeftEdgePosition >= 225;
        }

        const hobCutoutOnLeftSide = this.hobcutoutDistanceLeft != null && this.hobcutoutDistanceLeft < sinkLeftEdgePosition;

        // If hobcutout is on left side of sink, left funct. area requires atleast 245mm space
        if (hobCutoutOnLeftSide) {
            return sinkLeftEdgePosition - ((this.hobcutoutDistanceLeft ?? 0) + (this.hobcutoutWidth ?? 0)) >= 245;
        }

        return sinkLeftEdgePosition >= 225;
    }

    get functionalAreaRightAvailable() {
        const sinkRightEdgePosition = this.sinkDistanceRight ?? 0; // 1285

        if (!this.state?.hobcutout || this.state.hobcutout == "0") {
            return sinkRightEdgePosition >= 225;
        }

        const hobCutoutOnRightSide = this.hobcutoutDistanceRight != null && this.hobcutoutDistanceRight < sinkRightEdgePosition;

        // If hobcutout is on left side of sink, left funct. area requires atleast 245mm space
        if (hobCutoutOnRightSide) {
            return sinkRightEdgePosition - ((this.hobcutoutDistanceRight ?? 0) + (this.hobcutoutWidth ?? 0)) >= 245;
        }

        const sinkHobcutoutDistanceLeft = (this.sinkDistanceLeft ?? 0) - ((this.hobcutoutDistanceLeft ?? 0) + (this.hobcutoutWidth ?? 0));

        // If hobcutout is on other side, 100mm is enough
        return sinkRightEdgePosition >= 225 && sinkHobcutoutDistanceLeft >= 100;
    }

    get sink(): Sink {
        let sinks = this.config!.models.sinks.filter(s => s.code == this.state!.sink);

        let sinkConfiguration = sinks.find(s => s.handedness == this.state!.sinkHandedness) || sinks[0];

        //let sinkConfiguration = this.config.models.sinks.find(s => s.code == this.state.sink && s.handedness == this.state.sinkHandedness);

        if (!sinkConfiguration) {
            return {
                name: null,
                code: null,
                handedness: null,
                description: null,
                image: null,
                drawing: null,
                hasDimensions: false,
                length: 0,
                width: 0,
                fixedDistanceFront: null,
                sinkCount: 0,
                height: 0,
                margins: 40,
                leftHanded: true,
                centerPointOffset: 0
            }
        }

        return {
            name: sinkConfiguration.name,
            code: sinkConfiguration.code,
            handedness: this.state!.sinkHandedness,
            description: sinkConfiguration.description,
            image: sinkConfiguration.image,
            drawing: sinkConfiguration.drawing,
            length: sinkConfiguration.length,
            width: sinkConfiguration.width,
            fixedDistanceFront: sinkConfiguration.fixedDistanceFront,
            sinkCount: sinkConfiguration.sinkCount,
            height: sinkConfiguration.height,
            hasDimensions: sinkConfiguration.length > 0 && sinkConfiguration.width > 0,
            margins: 40,
            leftHanded: this.state!.sinkHandedness == "V",
            centerPointOffset: sinkConfiguration.centerPointOffset ?? 0,
        }
    }

    get sinkIsValid() { return (!this.state!.sink || this.sinkValidationMessages.length == 0) }
    get sinkHasPlacementError() {
        return this.hasCollision(this.sinkDistanceLeft, null)
            || !this.sinkIsValid
            || (this.sink.hasDimensions
                && ((this.sinkDistanceLeft ?? 0) < this.sink.margins || (this.sinkDistanceRight ?? 0) < this.sink.margins));
            }

    get sinkMargins() {
        // const rightFunctionalArea = this.state?.functionalArea == "L";
        // const leftFunctionalArea = this.state?.functionalArea == "R";
        // const hasHobCutout = this.state?.hobcutout && this.state.hobcutout != "0";

        // let hobCutoutOnFunctionalSide = false;

        // if (hasHobCutout) {
        //     if (leftFunctionalArea) {
        //         hobCutoutOnFunctionalSide = (this.hobcutoutDistanceLeft ?? 0) < (this.sinkDistanceLeft ?? 0);
        //     }
        //     else if (rightFunctionalArea) {
        //         hobCutoutOnFunctionalSide = (this.hobcutoutDistanceRight ?? 0) < (this.sinkDistanceRight ?? 0);
        //     }
        // }

        const margins = {
            top: this.sink.margins,
            bottom: this.sink.margins,
            left: this.sink.margins,
            right: this.sink.margins
        };

        // if (hasHobCutout && rightFunctionalArea)
        //     margins.right = hobCutoutOnFunctionalSide ? 245 : 100;

        // if (hasHobCutout && leftFunctionalArea)
        //     margins.left = hobCutoutOnFunctionalSide ? 245 : 100;

        return margins;
    }

    get sinkValidationMessages() {
        let sinks = this.config!.models.sinks.filter(s => s.code == this.state!.sink);

        let sinkConfiguration = sinks.find(s => s.handedness == this.state!.sinkHandedness) || sinks[0];

        let messages = [];

        if (sinkConfiguration && sinkConfiguration.rules) {
            for (let rule of sinkConfiguration.rules) {
                let enabled = runExpr({
                    state: this.state,
                    ui: this.self,
                    selfKey: "sink"
                }, rule.expression);

                if (!enabled && messages.indexOf(rule.resourceKey) == -1) {
                    messages.push(rule.resourceKey);
                }
            }
        }

        return messages;
     }

    get sinkSelectionsComplete() {
        if (this.state!.sink && !this.sink.length)
            return true;

        return [ this.state!.sink, this.state!.sinkDistanceX, this.state!.faucetHole, this.state!.extraHole, this.state!.extraHole2, this.sinkIsValid, !this.sinkHasPlacementError ].filter(s => !s).length == 0;
    }

    get sinkDistanceFront() { return this.sink.fixedDistanceFront || ((this.worktopWidth - this.sink.width) / 2); }

    get sinkDistanceBack() { return (this.worktopWidth - this.sink.width - this.sinkDistanceFront); }

    get sinkDistanceLeftMinimum() {
        return this.getSafeSinkDistanceX(0, true);
    }

    get sinkDistanceRightMinimum() {
        return this.getSafeSinkDistanceX(0, false);
    }

    get sinkDistanceLeft(): number | null {
        if (!this.state!.sinkDistanceX)
            return null;

        // console.log("this.sink.leftHanded", this.sink.leftHanded);
        // console.log("this.state!.sinkDistanceX", this.state!.sinkDistanceX);
        // console.log("this.worktopLength", this.worktopLength);
        // console.log("this.sink.length", this.sink.length);

        // console.log("get_sinkDistanceLeft", this.sink.leftHanded
        //     ? parseInt(this.state!.sinkDistanceX)
        //     : (this.worktopLength ?? 0)- this.sink.length - parseInt(this.state!.sinkDistanceX));

        return this.sink.leftHanded
            ? parseInt(this.state!.sinkDistanceX)
            : (this.worktopLength ?? 0)- this.sink.length - parseInt(this.state!.sinkDistanceX);
    }

    set sinkDistanceLeft(value: number) {
        let distanceLeft = this.getSafeSinkDistanceX(value, true);

        if (this.state!.sinkHandedness != "V") {
            let sinks = this.config!.models.sinks.filter(s => s.code == this.state!.sink);

            if (sinks.length == 1) {
                this.state!.sinkHandedness = "V";
            }
        }

        // console.log("set_sinkDistanceLeft", this.state!.sinkHandedness == "V"
        //     ? padLeft(distanceLeft, 4, "0")
        //     : padLeft((this.worktopLength ?? 0)- this.sink.length - (distanceLeft ?? 0), 4, "0"));

        this.state!.sinkDistanceX = this.state!.sinkHandedness == "V"
            ? padLeft(distanceLeft, 4, "0")
            : padLeft((this.worktopLength ?? 0)- this.sink.length - (distanceLeft ?? 0), 4, "0");
    }

    get sinkDistanceLeftCenter(): number | null {
        if (!this.state!.sinkDistanceX)
            return null;

        return this.sink.leftHanded
            ? parseInt(this.state!.sinkDistanceX) + this.sink.length / 2
            : (this.worktopLength ?? 0)- this.sink.length - parseInt(this.state!.sinkDistanceX) + this.sink.length / 2
    }
    set sinkDistanceLeftCenter(value: number) {
        let edgeValue = value - this.sink.length / 2;
        let distanceLeft = this.getSafeSinkDistanceX(edgeValue, true);

        if (this.state!.sinkHandedness != "V") {
            let sinks = this.config!.models.sinks.filter(s => s.code == this.state!.sink);

            if (sinks.length == 1) {
                this.state!.sinkHandedness = "V";
            }
        }

        this.state!.sinkDistanceX = this.state!.sinkHandedness == "V"
            ? padLeft(distanceLeft, 4, "0")
            : padLeft((this.worktopLength ?? 0)- this.sink.length - (distanceLeft ?? 0), 4, "0");
    }

    get sinkDistanceRight(): number | null {
        if (!this.state!.sinkDistanceX)
            return null;

        let val: number | null = !this.sink.leftHanded
            ? parseInt(this.state!.sinkDistanceX)
            : (this.worktopLength ?? 0) - this.sink.length - parseInt(this.state!.sinkDistanceX);

        val = this.getSafeSinkDistanceX(val, false);

        return val;
    }

    set sinkDistanceRight(value: number) {
        let distanceRight = this.getSafeSinkDistanceX(value, false);

        if (this.state!.sinkHandedness != "O") {
            let sinks = this.config!.models.sinks.filter(s => s.code == this.state!.sink);

            if (sinks.length == 1) {
                this.state!.sinkHandedness = "O";
            }
        }

        this.state!.sinkDistanceX = this.state!.sinkHandedness != "V"
            ? padLeft(distanceRight, 4, "0")
            : padLeft((this.worktopLength ?? 0)- this.sink.length - (distanceRight ?? 0), 4, "0");
    }

    get sinkDistanceRightCenter(): number | null {
        if (!this.state!.sinkDistanceX)
            return null;

        return !this.sink.leftHanded
            ? parseInt(this.state!.sinkDistanceX) + this.sink.length / 2
            : (this.worktopLength ?? 0)- this.sink.length - parseInt(this.state!.sinkDistanceX) + this.sink.length / 2
    }
    set sinkDistanceRightCenter(value: number) {
        let edgeValue = value - this.sink.length / 2;
        let distanceRight = this.getSafeSinkDistanceX(edgeValue, false);

        if (this.state!.sinkHandedness != "O") {
            let sinks = this.config!.models.sinks.filter(s => s.code == this.state!.sink);

            if (sinks.length == 1) {
                this.state!.sinkHandedness = "O";
            }
        }

        this.state!.sinkDistanceX = this.state!.sinkHandedness != "V"
            ? padLeft(distanceRight, 4, "0")
            : padLeft((this.worktopLength ?? 0)- this.sink.length - (distanceRight ?? 0), 4, "0");
    }

    getAvailableSinkSpace() {
        if (!this.state!.hobcutout || this.state!.hobcutout == "0" || (this.hobcutoutLength ?? 0) <= 0 || (this.hobcutoutWidth ?? 0) <= 0 || !this.hobcutoutDistanceLeft || this.hobcutoutDistanceLeft <= 0)
            return this.worktopLength;

        return Math.max(this.hobcutoutDistanceLeft - this.sink.margins - this.hobcutoutMargins,
                (this.hobcutoutDistanceRight ?? 0) - this.sink.margins - this.hobcutoutMargins);
    }

    clearFocusedField() {
        this.focusedFieldName = null;
    }

    get drawing(): Drawing {
        let containerLength: number = 5000;
        let containerWidth: number = 1500;

        let drawingReady = [ this.worktopWidth, this.worktopHeight, this.worktopLength ]
            .filter(d => d == null || isNaN(d) || d == 0).length == 0;

        let hobcutout = this.config!.models.hobcutouts.find(o => o.code == this.state!.hobcutout);

        let distances: DrawingDistance[] = [];

        let gapElement = null;

        //Sink length bar
        if (this.sink.hasDimensions) {
            distances.push({
                cssPositionLeft: (this.sinkDistanceLeft ?? 0) / (this.worktopLength ?? 0) * 100,
                positionLeftValue: this.sinkDistanceLeft ?? 0,
                cssWidth: this.sink.length / (this.worktopLength ?? 0) * 100,
                widthValue: this.sink.length
            });
        }

        //Hob cutout bar
        if (this.state!.hobcutout == "1" && (this.hobcutoutLength ?? 0) > 0) {
            distances.push({
                cssPositionLeft: (this.hobcutoutDistanceLeft ?? 0)  / (this.worktopLength ?? 0) * 100,
                positionLeftValue: this.hobcutoutDistanceLeft ?? 0,
                cssWidth: (this.hobcutoutLength ?? 0) / (this.worktopLength ?? 0) * 100,
                widthValue: this.hobcutoutLength ?? 0
            });
        }

        if (distances.length > 0) {
            let sortedDistances = distances.sort((a, b) => a.positionLeftValue - b.positionLeftValue);
            let lastDistanceElement = sortedDistances[sortedDistances.length - 1];
            let firstDistanceElement = sortedDistances[0];

            //Gap between sink and hobcutout
            if (distances.length == 2) {
                gapElement = {
                    cssPositionLeft: firstDistanceElement.cssPositionLeft + firstDistanceElement.cssWidth,
                    positionLeftValue: firstDistanceElement.positionLeftValue + firstDistanceElement.widthValue,
                    cssWidth: (lastDistanceElement.positionLeftValue - (firstDistanceElement.positionLeftValue + firstDistanceElement.widthValue)) / (this.worktopLength ?? 0) * 100,
                    widthValue: lastDistanceElement.positionLeftValue - (firstDistanceElement.positionLeftValue  + firstDistanceElement.widthValue)
                };

                distances.push(gapElement);
            }

            //Left edge of worktop to left edge of sink or hobcutout, which ever is leftmost
            distances.push({
                cssPositionLeft: 0,
                positionLeftValue: 0,
                cssWidth: firstDistanceElement.cssPositionLeft,
                widthValue: firstDistanceElement.positionLeftValue
            });

            let rightEdgeWidthValue = ((this.worktopLength ?? 0)- (lastDistanceElement.positionLeftValue + lastDistanceElement.widthValue));

            //Right edge of worktop to right edge of sink or hobcutout, which ever is rightmost
            distances.push({
                cssPositionLeft: lastDistanceElement.cssPositionLeft + lastDistanceElement.cssWidth,
                positionLeftValue: (this.worktopLength ?? 0)- (lastDistanceElement.positionLeftValue + lastDistanceElement.widthValue),
                cssWidth: rightEdgeWidthValue / (this.worktopLength ?? 0) * 100,
                widthValue: rightEdgeWidthValue
            });
        }

        let functionalAreaLeftValue = 0;
        let functionalAreaBackValue = (this.sinkDistanceBack ?? 0) - 91;
        let functionalAreaLengthValue = this.sink.length + 216;
        let functionalAreaFrontValue = this.worktopWidth - (this.sink.width + 101) - functionalAreaBackValue;

        let functionalAreaWidth = this.sink.width + 101;
        let functionalAreaBack = functionalAreaBackValue / (this.worktopWidth ?? 0) * 100;
        let functionalAreaFront = functionalAreaFrontValue / (this.worktopWidth ?? 0) * 100;

        if (this.state?.functionalArea == "L") {
            functionalAreaLeftValue = (this.sinkDistanceLeft ?? 0) - 200;
        }
        else if (this.state?.functionalArea == "R") {
            functionalAreaLeftValue = (this.sinkDistanceLeft ?? 0) - 16;
        }

        const functionalAreaLeft = functionalAreaLeftValue / (this.worktopLength ?? 0) * 100;
        const functionalAreaLength = functionalAreaLengthValue / (this.worktopLength ?? 0) * 100;



        // if (this.state?.functionalArea && this.state.functionalArea != "0") {
        //     const functionalAreaWidth = this.sink.length + 236;
        //     const functionalAreaHeight = this.sink.height + 101;
        //     const functionalAreaLeft = (this.sinkDistanceLeft ?? 0) - 20;

        //     distances.push({
        //         cssPositionLeft: functionalAreaLeft / (this.worktopLength ?? 0) * 100,
        //         positionLeftValue: functionalAreaLeft,
        //         cssWidth: functionalAreaWidth / (this.worktopLength ?? 0) * 100,
        //         widthValue: functionalAreaWidth
        //     });
        // }

        return {
            ready: drawingReady,

            containerWidth: containerWidth,
            containerLength: containerLength,
            worktopWidth: this.worktopWidth / containerWidth * 100,
            worktopHeight: (17 + (this.worktopHeight - 20) / 2) / 2,
            worktopLength: this.worktopLength! / containerLength * 100,

            sinkLength: this.sink.length / (this.worktopLength ?? 0) * 100,
            sinkWidth: this.sink.width / this.worktopWidth * 100,

            sinkDistanceLeft: (this.sinkDistanceLeft ?? 0) / (this.worktopLength ?? 0) * 100,
            sinkDistanceLeftCenter: (this.sinkDistanceLeftCenter ?? 0) / (this.worktopLength ?? 0) * 100,
            sinkDistanceRight: (this.sinkDistanceRight ?? 0) / (this.worktopLength ?? 0) * 100,
            sinkDistanceRightCenter: (this.sinkDistanceRightCenter ?? 0) / (this.worktopLength ?? 0) * 100,
            sinkDistanceFront: this.sinkDistanceFront / this.worktopWidth * 100,
            sinkDistanceBack: this.sinkDistanceBack / this.worktopWidth * 100,
            sinkDrawing: this.sink.drawing ?? "",

            gapDistanceLeft: gapElement ? gapElement.cssPositionLeft : 0,
            gapLength: gapElement ? gapElement.cssWidth : 0,
            gapValue: gapElement ? gapElement.widthValue : 0,

            hobcutoutWidth: (this.hobcutoutWidth ?? 0)  / this.worktopWidth * 100,
            hobcutoutLength: (this.hobcutoutLength ?? 0) / (this.worktopLength ?? 0) * 100,
            hobcutoutDistanceFront: this.hobcutoutDistanceFront  / this.worktopWidth * 100,
            hobcutoutDistanceBack: this.hobcutoutDistanceBack / this.worktopWidth * 100,
            hobcutoutDistanceLeft: (this.hobcutoutDistanceLeft ?? 0)  / (this.worktopLength ?? 0) * 100,
            hobcutoutDistanceLeftCenter: (this.hobcutoutDistanceLeftCenter ?? 0)  / (this.worktopLength ?? 0) * 100,
            hobcutoutDistanceRight: (this.hobcutoutDistanceRight ?? 0)  / (this.worktopLength ?? 0) * 100,
            hobcutoutDistanceRightCenter: (this.hobcutoutDistanceRightCenter ?? 0)  / (this.worktopLength ?? 0) * 100,
            hobcutoutDrawing: hobcutout ? hobcutout.drawing : null,

            functionalAreaLeft,
            functionalAreaBack,
            functionalAreaWidth,
            functionalAreaLength,
            functionalAreaFront,

            functionalAreaLeftValue,
            functionalAreaLengthValue,
            functionalAreaBackValue,
            functionalAreaFrontValue,

            safetyAreaWidth: (this.worktopWidth - 30) / this.worktopWidth * 100,
            safetyAreaLength: ((this.sink.length ?? 0) + 50) / (this.worktopLength ?? 0) * 100,
            safetyAreaLeft: ((this.sinkDistanceLeft ?? 0) - 25) / (this.worktopLength ?? 0) * 100,

            summaryDistances: distances
        };
    }

    get functionalAreaAvailable(): boolean {
        return this.state?.productFamily == "one" && 
            (!this.sink.code || this.sink.sinkCount > 0) &&
            (!this.state.material || this.state.material == "F");
    }

    t(key: string, ...replacementValues: any[]) {

        if (!replacementValues || !replacementValues.length)
            return this.translations[key] || `[${key}]`;

        let result = this.translations[key] || `[${key}]`;

        for(let i = 0; i < replacementValues.length; i++) {
            result = result.replace(`{${i}}`, replacementValues[i]);
        }

        return result;
    }

    showNewWorktopOptionsAvailableTip: boolean = false;

    showNewWorktopOptionsAvailable() {
        this.showNewWorktopOptionsAvailableTip = true;
        setTimeout(() => this.showNewWorktopOptionsAvailableTip = false, 4_000);
    }
}

function padLeft(source: number | null, n: number | null, padStr: string){
    return Array((n ?? 0)-String((source ?? 0).toFixed(0)).length+1).join(padStr||"0")+(source ?? 0).toFixed(0);
}


export const useState = defineStore("state", () => {
    const state = new State();

    return {
        state
    }
});

export const useCart = defineStore("cart", () => {
    const cart = new Cart();

    return {
        cart
    }
});

export const useUI = defineStore("ui", () => {
    let ui = new UI();

    return {
        ui,
    }
});

export const useConfiguration = defineStore("configuration", () => {
    const configuration = {} as Configuration;

    return {
        configuration
    }
});

// const defaultStore: Store = {
//     configuration: null,
//     ui: null,
//     cart: null,
//     ready: false,
//     state: null
// };

// const storeVm: Vue = new Vue({ data: { store: defaultStore } });

// export const store: Store = storeVm.$data["store"];